import React from 'react';
import { Box, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Apps as AppsIcon } from '@material-ui/icons';
import { AppItem } from './AppItem';
import MailchimpLogo from '../../../../../../assets/apps/mailchimp_logo.svg';
import { ReactComponent as MailchimpIcon } from '../../../../../../assets/apps/mailchimp_icon.svg';
import SalesforceLogo from '../../../../../../assets/apps/salesforce_logo.svg';
import { ReactComponent as SalesforceIcon } from '../../../../../../assets/apps/salesforce_icon.svg';
import { ReactComponent as CampaignMonitorIcon } from '../../../../../../assets/apps/campaign_monitor.svg';
import CampaignMonitorLogo from '../../../../../../assets/apps/campaign_monitor_logo.svg';
import { ReactComponent as StripeIcon } from '../../../../../../assets/apps/stripe_icon.svg';
import StripeLogo from '../../../../../../assets/apps/stripe_logo.svg';
import { useCommonStyles } from './commonStyles';
import { INTEGRATION_APPS } from './admin-console-apps.gql';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { GetIntegrationAppsType } from './__generated__/GetIntegrationAppsType';
import { App } from './AppTypes';
import { AlertCard, getAlertCardColors } from '../../../../../../common/cards/alert-card/AlertCard';
import { useOrgInfo } from '../../../../../../global/auth/use-org-info/use-org-info';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    text: {
      fontWeight: 400,
      color: theme.color.grey.neutral200,
      fontSize: '0.875rem',
    },
    footer: {
      padding: '2.375rem',
    },
    sectionHeader: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      marginBottom: '1rem',
      fontWeight: 400,
    },
    infoBody: {
      color: theme.alert.blue.dark,
      fontSize: '14px',
      fontWeight: 400,
    },
  })
);

export type APP_OPTIONS_TYPE = {
  id: string;
  name: string;
  logo: string;
  description: string;
  integrationDescription: string;
  disabled?: boolean;
  icon: React.SFC<React.SVGProps<SVGSVGElement>>;
};

export const APP_OPTIONS = [
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    logo: MailchimpLogo,
    description:
      'Mailchimp is an all-in-one marketing platform that helps you manage and talk to your clients, customers, and other interested parties.',
    integrationDescription:
      'Integrating with Mailchimp will allow Volaby to import and update volunteers into your audience, with options to segment audiences by programs and activities.',
    icon: MailchimpIcon,
  },
  {
    id: 'stripe',
    name: 'Stripe',
    logo: StripeLogo,
    description: 'Accept payments from volunteers for equipment, training materials, certifications, and event fees.',
    integrationDescription:
      'Integrating with Stripe will allow you to create payment tasks that volunteers must complete before accessing activities or programs.',
    icon: StripeIcon,
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    logo: SalesforceLogo,
    description: 'Powerful CRM platform for managing volunteer relationships and tracking engagement.',
    integrationDescription:
      'Integrating with Salesforce will allow you to sync volunteer data, track interactions, and manage your volunteer pipeline through a comprehensive CRM system.',
    icon: SalesforceIcon,
    disabled: true,
  },
  {
    id: 'campaign-monitor',
    name: 'Campaign Monitor',
    logo: CampaignMonitorLogo,
    description: 'Professional email marketing platform for creating beautiful campaigns and newsletters.',
    integrationDescription:
      'Integrating with Campaign Monitor will allow you to send targeted email campaigns to volunteers, track engagement, and manage email marketing automation.',
    icon: CampaignMonitorIcon,
    disabled: true,
  },
];

const APP_OPTIONS_MAP = {
  mailchimp: {
    id: 'mailchimp',
    name: 'Mailchimp',
    logo: MailchimpLogo,
    description:
      'Mailchimp is an all-in-one marketing platform that helps you manage and talk to your clients, customers, and other interested parties.',
    integrationDescription:
      'Integrating with Mailchimp will allow Volaby to import and update volunteers into your audience, with options to segment audiences by programs and activities.',
    icon: MailchimpIcon,
  },
  stripe: {
    id: 'stripe',
    name: 'Stripe',
    logo: StripeLogo,
    description: 'Accept payments from volunteers for equipment, training materials, certifications, and event fees.',
    integrationDescription:
      'Integrating with Stripe will allow you to create payment tasks that volunteers must complete before accessing activities or programs.',
    icon: StripeIcon,
  },
  salesforce: {
    id: 'salesforce',
    name: 'Salesforce',
    logo: SalesforceLogo,
    description: 'Powerful CRM platform for managing volunteer relationships and tracking engagement.',
    integrationDescription:
      'Integrating with Salesforce will allow you to sync volunteer data, track interactions, and manage your volunteer pipeline through a comprehensive CRM system.',
    icon: SalesforceIcon,
    disabled: true,
  },
  // eslint-disable-next-line
  campaign_monitor: {
    id: 'campaign-monitor',
    name: 'Campaign Monitor',
    logo: CampaignMonitorLogo,
    description: 'Professional email marketing platform for creating beautiful campaigns and newsletters.',
    integrationDescription:
      'Integrating with Campaign Monitor will allow you to send targeted email campaigns to volunteers, track engagement, and manage email marketing automation.',
    icon: CampaignMonitorIcon,
    disabled: true,
  },
} as { [app: string]: APP_OPTIONS_TYPE };

export const AdminConsoleApps = () => {
  const classes = useStyles();
  const commonStyles = useCommonStyles();
  const orgInfo = useOrgInfo();

  const { data } = useCampfireQuery<GetIntegrationAppsType, undefined>(INTEGRATION_APPS);
  const integrationApps = data?.wm?.integrationApps || [];
  const integrationAppsMap = integrationApps.reduce((acc, cur) => ({ ...acc, [cur.name]: cur }), {});

  // Check if organization is in trial phase
  const isInTrial = orgInfo?.inTrial || false;

  const { darkTextColor: warningDarkTextColor } = getAlertCardColors('warning');

  const apps = (data?.wm?.availableApps || [])
    .map(
      (app) =>
        ({
          ...APP_OPTIONS_MAP[app],
          active: app in integrationAppsMap,
          // Disable all apps during trial phase (except already active ones)
          disabled: APP_OPTIONS_MAP[app].disabled || (isInTrial && !(app in integrationAppsMap)),
        } as App)
    )
    .sort((a, b) => {
      // Sort disabled apps (coming soon) to the bottom
      if (a.disabled && !b.disabled) return 1;
      if (!a.disabled && b.disabled) return -1;
      return 0;
    });
  const activeApps = apps.filter((app) => app.active);
  const hasNoIntegrations = activeApps.length === 0;

  return (
    <Box className={commonStyles.container}>
      <Box className={commonStyles.heading}>
        <Typography variant='h2' className={commonStyles.headingTitle}>
          Integrations
        </Typography>
        <Typography variant={'body2'}>
          Connect Volaby with external applications to streamline your volunteer management workflow.
        </Typography>
      </Box>

      {!hasNoIntegrations && (
        <Box className={commonStyles.containerPanel}>
          <Typography className={classes.sectionHeader}>
            <AppsIcon style={{ fontSize: '14px', paddingRight: '5px' }} />
            Your Active Integrations
          </Typography>
          <Typography variant={'body2'} className={classes.sectionDescription}>
            Manage your currently connected applications and their configurations.
          </Typography>
          <AlertCard variant='info' title='About active integrations'>
            <Typography className={classes.infoBody}>
              These integrations are currently active and working with your Volaby account. You can manage their
              settings, view connection status, or disconnect them if needed.
            </Typography>
          </AlertCard>
          {activeApps.map((app) => (
            <AppItem app={app} key={app.id} />
          ))}
        </Box>
      )}

      <Box className={commonStyles.containerPanel}>
        <Typography className={classes.sectionHeader}>
          <AppsIcon style={{ fontSize: '14px', paddingRight: '5px' }} />
          Available Apps
        </Typography>
        <Typography variant={'body2'} className={classes.sectionDescription}>
          {hasNoIntegrations
            ? "You haven't setup any integrations yet. Choose from the applications below to get started."
            : 'Connect additional applications to enhance your volunteer management capabilities.'}
        </Typography>

        {isInTrial && (
          <AlertCard variant='warning' title='Trial Limitations'>
            <Typography style={{ fontSize: '14px', fontWeight: 400, color: warningDarkTextColor }}>
              Integration setup is not available during your trial period. To connect these applications to your Volaby
              account, please upgrade to a paid plan. Already active integrations will continue to work normally.
            </Typography>
          </AlertCard>
        )}

        {apps
          .filter((app) => !app.active)
          .map((app) => (
            <AppItem app={app} key={app.id} />
          ))}

        <Box className={classes.footer}>
          <Typography className={classes.text} style={{ textAlign: 'center', paddingTop: '1em' }}>
            There&apos;s more to come! We&apos;ll be adding integration support for more apps in the near future. Get in
            contact if there&apos;s something you&apos;d like to see.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
